import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ReferralTableItem } from "./ReferralTableItem";
import { ReferralTableSkeleton } from "./ReferralTableSkeleton";
import { type ReferralUIData } from "@/lib/schema";

interface ReferralsTableProps {
  referrals: ReferralUIData[];
  onViewReferral: (referral: ReferralUIData) => void;
  itemsPerPage?: number;
  showPagination?: boolean;
  isLoading?: boolean;
}

export function ReferralsTable({ 
  referrals, 
  onViewReferral, 
  itemsPerPage = 10,
  showPagination = true,
  isLoading = false
}: ReferralsTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(itemsPerPage);

  // Calculate pagination
  const totalPages = Math.ceil(referrals.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentReferrals = referrals.slice(startIndex, endIndex);

  // Reset to page 1 when referrals change
  useMemo(() => {
    setCurrentPage(1);
  }, [referrals.length]);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };


  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  // Show skeleton loading state
  if (isLoading) {
    return <ReferralTableSkeleton rows={pageSize} />;
  }

  // Show empty state
  if (referrals.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <p className="text-gray-500 space-grotesk">No referrals found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Desktop Table Header */}
      <div className="hidden lg:grid grid-cols-6 py-3 px-4 gap-2 bg-[#F6F6F6] border border-[#EAEAEA] text-[10px] font-semibold text-gray-500 uppercase leading-[140%] tracking-normal">
        <div>CANDIDATE</div>
        <div>JOB TITLE</div>
        <div>STATUS</div>
        <div>REFERRAL DATE</div>
        <div>REWARD</div>
        <div>ACTIONS</div>
      </div>

      {/* Table Body */}
      <div className="divide-y divide-gray-200">
        {currentReferrals.map((referral, index) => (
          <ReferralTableItem
            key={referral.id}
            referral={referral}
            index={startIndex + index}
            onViewReferral={onViewReferral}
          />
        ))}
      </div>

      {/* Pagination */}
      {showPagination && referrals.length > pageSize && (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-end">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="space-grotesk text-gray-600 disabled:text-gray-400"
            >
              Previous
            </Button>

            <div className="flex items-center gap-1">
              {getPageNumbers().map((page) => (
                <Button
                  key={page}
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className={`w-10 h-10 p-0 space-grotesk rounded-lg font-medium ${
                    currentPage === page 
                      ? "bg-orange-500 text-white" 
                      : "text-gray-600 bg-white border border-[#EAEAEA]"
                  }`}
                >
                  {page.toString().padStart(2, '0')}
                </Button>
              ))}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="space-grotesk text-gray-600 disabled:text-gray-400"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
